/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {
    Flex,
    Stack,
    Grid,
    GridItem,
    Container,
    Skeleton,
    Text,
    Heading,
    Box,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import Seo from '../../../components/seo'
import {useBrand} from '../../../contexts/brand-context'

const CartItemSkeleton = () => {
    const styles = useMultiStyleConfig('Cart')

    return (
        <Stack {...styles.cartSkeletonItemCard}>
            <Flex {...styles.cartSkeletonItemRow}>
                <Skeleton {...styles.cartSkeletonItemImage} />
                <Stack {...styles.cartSkeletonItemMetaStack}>
                    <Skeleton width="80px" height="20px" />
                    <Skeleton
                        width={{base: '180px', sm: '180px', md: '280px', lg: '280px'}}
                        height={3}
                    />
                    <Skeleton
                        width={{base: '120px', sm: '120px', md: '140px', lg: '140px'}}
                        height={3}
                    />
                </Stack>
            </Flex>
        </Stack>
    )
}

const CartSkeleton = () => {
    const styles = useMultiStyleConfig('Cart')
    const {isGarage} = useBrand()
    const {formatMessage} = useIntl()

    const cartPageTitle = isGarage
        ? formatMessage({
              id: 'cart.page.title.garage',
              defaultMessage: 'My Shopping Cart | Garage'
          })
        : formatMessage({
              id: 'cart.page.title.dynamite',
              defaultMessage: 'My Shopping Cart | Dynamite'
          })
    const cartPageDescription = isGarage
        ? formatMessage({
              id: 'cart.page.description.garage',
              defaultMessage: "Find everything you're looking for? Review your cart here before checking out."
          })
        : formatMessage({
              id: 'cart.page.description.dynamite',
              defaultMessage: "Find everything you're looking for? Review your cart here before checking out."
          })

    return (
        <Box {...styles.cartSkeletonRoot}>
            <Seo title={cartPageTitle} description={cartPageDescription}/>
            <Container
                {...styles.cartSkeletonContainer}
                data-testid="sf-cart-skeleton"
            >
                <Grid {...styles.cartSkeletonGrid}>
                    <GridItem>
                        <Stack {...styles.cartSkeletonMainStack}>
                            <Text {...styles.cartSkeletonTitleText}>
                                <FormattedMessage
                                    defaultMessage="Cart"
                                    id="cart_skeleton.title.cart"
                                />
                            </Text>
                            <CartItemSkeleton />
                            <CartItemSkeleton />
                        </Stack>
                    </GridItem>
                    <GridItem {...styles.cartSkeletonSidebar}>
                        <Stack {...styles.cartSkeletonSidebarStack}>
                            <Heading {...styles.cartSkeletonSummaryHeading}>
                                <FormattedMessage
                                    defaultMessage="Order Summary"
                                    id="cart_skeleton.heading.order_summary"
                                />
                            </Heading>
                            <Stack {...styles.cartSkeletonSummaryList}>
                                <Skeleton
                                    width={{base: '180px', sm: '180px', md: '280px', lg: '280px'}}
                                    height={4}
                                />
                                <Skeleton width="120px" height={4} />
                                <Skeleton
                                    width={{base: '180px', sm: '180px', md: '280px', lg: '280px'}}
                                    height={4}
                                />
                                <Skeleton width="120px" height={4} />
                            </Stack>
                        </Stack>
                    </GridItem>
                </Grid>
            </Container>
        </Box>
    )
}

export default CartSkeleton
